import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { posthog, whenPostHogReady } from '@/lib/analytics/client';

// Hook for user identification
export function usePostHogAuth() {
  const { user, isSignedIn, isLoaded } = useUser();

  useEffect(() => {
    // Wait for Clerk: while it loads, isSignedIn is undefined — resetting
    // there rotated the distinct_id on EVERY page load, minting a fresh
    // anonymous person for the pre-identify pageviews and rewriting the
    // shared .specstory.com cookie with an anonymous id.
    if (!isLoaded) return;
    if (isSignedIn && user) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        username: user.username,
      });
      return;
    }
    // Signed out: only reset when a previous user's identity is actually
    // present. Resetting unconditionally rotated the anonymous distinct_id on
    // every signed-out page load — a fresh "person" per visit, and an
    // anonymous journey that could never merge into the eventual signup.
    let cancelled = false;
    whenPostHogReady().then((ph) => {
      if (cancelled) return;
      if (ph.get_property('$user_state') === 'identified') ph.reset();
    });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, user]);
}

// Hook for page view tracking
export function usePostHogPageView() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (typeof window !== 'undefined' && posthog) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        $pathname: location.pathname,
        $search: location.search,
        $navigation_type: navigationType,
      });
    }
  }, [location, navigationType]);
}